import { MIN_RECORD_SAVE_SECONDS } from "@timeghost/shared";
import {
	addDays,
	addMinutes,
	eachDayOfInterval,
	endOfISOWeek,
	formatISO,
	startOfDay,
	startOfISOWeek,
	startOfMinute,
} from "date-fns";
import sorter from "fluent-sorter";
import { formatLocalClock } from "@/lib/account/format-profile-date-time";
import { getProfileFormat } from "@/lib/account/use-profile-format";
import { parseDateInput } from "@/lib/date-iso";
import type { SettingsProject } from "@/lib/projects/types";
import { sortEntriesOldestFirst } from "./entry-sort";
import type { TimeClientRef, TimeDayGroup, TimeProjectRef, TimeRow, TimeTagRef, TimeTaskRef } from "./types";

const asRecord = (value: unknown): Record<string, unknown> | null => {
	if (!value || typeof value !== "object") {
		return null;
	}

	return value as Record<string, unknown>;
};

const asString = (value: unknown): string | null => (typeof value === "string" && value.length > 0 ? value : null);

const asNumber = (value: unknown): number => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	if (typeof value === "string" && value.length > 0) {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : 0;
	}

	return 0;
};

const asBoolean = (value: unknown): boolean => value === true;

const parseClient = (value: unknown): TimeClientRef | null => {
	const client = asRecord(value);
	const id = asString(client?.id);
	const name = asString(client?.name);
	if (!(id && name)) {
		return null;
	}

	return { id, name };
};

const parseProject = (value: unknown): TimeProjectRef | null => {
	const project = asRecord(value);
	const id = asString(project?.id);
	const name = asString(project?.name);
	if (!(id && name)) {
		return null;
	}

	return {
		id,
		name,
		color: asString(project?.color),
		useAsDefault: asBoolean(project?.useAsDefault),
		completed: asBoolean(project?.completed),
		billable: asBoolean(project?.billable),
		externalId: asString(project?.externalId ?? project?.external_id),
		client: parseClient(project?.client),
	};
};

const parseTask = (value: unknown): TimeTaskRef | null => {
	const task = asRecord(value);
	const id = asString(task?.id);
	const name = asString(task?.name);
	if (!(id && name)) {
		return null;
	}

	return { id, name };
};

const parseTags = (value: unknown): readonly TimeTagRef[] => {
	if (!Array.isArray(value)) {
		return [];
	}

	return value.flatMap((item) => {
		const tag = asRecord(item);
		const id = asString(tag?.id);
		const name = asString(tag?.name);
		if (!(id && name)) {
			return [];
		}

		return [{ id, name }];
	});
};

const asIso = (value: unknown): string | null => {
	if (typeof value === "string" && value.length > 0) {
		return value;
	}
	if (value instanceof Date && !Number.isNaN(value.getTime())) {
		return value.toISOString();
	}
	if (typeof value === "number" && Number.isFinite(value)) {
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? null : date.toISOString();
	}
	return null;
};

export const parseTimeRow = (value: unknown): TimeRow | null => {
	const entity = asRecord(value);
	const id = asString(entity?.id);
	const start = asIso(entity?.start);
	if (!(id && start)) {
		return null;
	}

	const nameValue = entity?.name;
	const inputMode = entity?.inputMode === "duration" || entity?.inputMode === "range" ? entity.inputMode : undefined;

	return {
		id,
		name: typeof nameValue === "string" ? nameValue : null,
		start,
		end: asIso(entity?.end),
		timeDiff: asNumber(entity?.timeDiff),
		billable: asBoolean(entity?.billable),
		inputMode,
		project: parseProject(entity?.project),
		task: parseTask(entity?.task),
		client: parseClient(entity?.client),
		tags: parseTags(entity?.tags),
		hasTimeOverlap: asBoolean(entity?.hasTimeOverlap),
		currentUserCanEdit: entity?.currentUserCanEdit !== false,
		userId: asString(asRecord(entity?.user)?.id) ?? asString(entity?.userId),
	};
};

export const isOwnedByUser = (ownerId: string | null | undefined, userId: string): boolean =>
	!ownerId || ownerId === userId;

export const getIsoWeekRange = (anchor: Date = new Date()): { readonly start: Date; readonly end: Date } => ({
	start: startOfISOWeek(anchor),
	end: endOfISOWeek(anchor),
});

/** UTC ISO-8601 (`...Z`) for OData `$filter` and API range query keys. */
export const toApiDateBoundary = (date: Date): string => date.toISOString();

export { formatDuration } from "./parse-ms";

/** `timeFormat24h` omitted: follow the profile 12/24h setting. `_locale` is unused (AM/PM is language-independent), kept for callers. */
export const formatClockTime = (iso: string, _locale = "en", timeFormat24h?: boolean, showSeconds = false): string => {
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) {
		return showSeconds ? "--:--:--" : "--:--";
	}

	return formatLocalClock(date, {
		showSeconds,
		timeFormat24h: timeFormat24h ?? getProfileFormat().settings.timeFormat24h,
	});
};

/** Clock span for range entries; duration mode has no real start/end clock. */
export const formatProjectHourClock = (
	item: { readonly end: Date | null; readonly inputMode: "duration" | "range"; readonly start: Date },
	locale: string,
	timeFormat24h?: boolean
): string => {
	if (item.inputMode === "duration") {
		return "";
	}
	const start = formatClockTime(item.start.toISOString(), locale, timeFormat24h);
	if (!item.end) {
		return start;
	}
	return `${start} - ${formatClockTime(item.end.toISOString(), locale, timeFormat24h)}`;
};

/** Weekday in `locale`, date in the profile pattern. */
export const formatDayHeading = (date: Date, locale = "en"): string =>
	`${new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date)}, ${getProfileFormat().date(date)}`;

export const withOpenRecording = (times: readonly TimeRow[], running: TimeRow | null): readonly TimeRow[] => {
	if (!running || running.end) {
		return times;
	}
	const index = times.findIndex((time) => time.id === running.id);
	if (index === -1) {
		return [...times, running];
	}
	if (times[index] === running) {
		return times;
	}
	const next = [...times];
	next[index] = running;
	return next;
};

export const groupTimesByDay = (times: readonly TimeRow[]): readonly TimeDayGroup[] => {
	const groups = new Map<string, TimeDayGroup>();

	for (const time of times) {
		const date = new Date(time.start);
		if (Number.isNaN(date.getTime())) {
			continue;
		}

		const dayKey = formatISO(date, { representation: "date" });
		const existing = groups.get(dayKey);
		if (!existing) {
			groups.set(dayKey, {
				dayKey,
				date: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
				times: [time],
				timeSum: time.timeDiff,
				billableSum: time.billable ? time.timeDiff : 0,
			});
			continue;
		}

		groups.set(dayKey, {
			...existing,
			times: [...existing.times, time],
			timeSum: existing.timeSum + time.timeDiff,
			billableSum: existing.billableSum + (time.billable ? time.timeDiff : 0),
		});
	}

	return Array.from(groups.values())
		.map((group) => ({
			...group,
			times: sortEntriesOldestFirst(group.times),
		}))
		.toSorted(sorter<TimeDayGroup, string>((group) => group.dayKey, "asc"));
};

export const fillDayGroupsForRange = (
	groups: readonly TimeDayGroup[],
	range: { readonly start: Date; readonly end: Date },
	now = new Date()
): readonly TimeDayGroup[] => {
	const byKey = new Map(groups.map((group) => [group.dayKey, group]));
	const rangeStart = startOfDay(range.start);
	const rangeEnd = startOfDay(range.end);
	// Future weeks: only days with entries — no empty Mon–Sun placeholders.
	if (rangeStart > startOfDay(now)) {
		return groups.toSorted(sorter<TimeDayGroup, string>((group) => group.dayKey, "asc"));
	}

	const placeholderDays = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

	const filled = placeholderDays.map((day) => {
		const date = startOfDay(day);
		const dayKey = formatISO(date, { representation: "date" });
		return (
			byKey.get(dayKey) ?? {
				dayKey,
				date,
				times: [],
				timeSum: 0,
				billableSum: 0,
			}
		);
	});

	const filledKeys = new Set(filled.map((group) => group.dayKey));
	const extraGroups = groups.filter((group) => !filledKeys.has(group.dayKey));

	return [...filled, ...extraGroups].toSorted(sorter<TimeDayGroup, string>((group) => group.dayKey, "asc"));
};

export const findDefaultProject = (projects: readonly TimeProjectRef[]): TimeProjectRef | null =>
	projects.find((project) => project.useAsDefault && !project.completed) ??
	projects.find((project) => !project.completed) ??
	null;

export const toTimeProjectRef = (project: SettingsProject): TimeProjectRef | null => {
	if (!(project.id && project.name)) {
		return null;
	}
	return {
		id: project.id,
		name: project.name,
		color: project.color,
		useAsDefault: project.useAsDefault === true,
		completed: project.completed === true,
		billable: project.billable === true,
		externalId: project.externalId || null,
		client: project.client ? { id: project.client.id, name: project.client.name } : null,
	};
};

export const toTimeInputValue = (isoOrDate: string | Date): string => {
	const date = typeof isoOrDate === "string" ? new Date(isoOrDate) : isoOrDate;
	if (Number.isNaN(date.getTime())) {
		return "00:00";
	}

	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	return `${hours}:${minutes}`;
};

export const toDateInputValue = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

/** Combine calendar day (YYYY-MM-DD or ISO) + HH:mm into a local Date. */
export const combineDateInputWithTime = (dateValue: string, timeValue: string): Date | null => {
	const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeValue);
	const day = parseDateInput(dateValue);
	if (!(timeMatch && day)) {
		return null;
	}

	const next = new Date(
		day.getFullYear(),
		day.getMonth(),
		day.getDate(),
		Number(timeMatch[1]),
		Number(timeMatch[2]),
		0,
		0
	);
	return Number.isNaN(next.getTime()) ? null : next;
};

/** When end is earlier than start, treat it as the next calendar day (overnight range). */
export const advanceEndIfBeforeStart = (start: Date, end: Date): Date =>
	end.getTime() < start.getTime() ? addDays(end, 1) : end;

/** Apply HH:mm onto the calendar day of `dayIso` (keeps entry on same day). */
export const combineDayWithTime = (dayIso: string, timeValue: string): Date | null => {
	const match = /^(\d{2}):(\d{2})$/.exec(timeValue);
	if (!match) {
		return null;
	}

	const base = new Date(dayIso);
	if (Number.isNaN(base.getTime())) {
		return null;
	}

	const hours = Number(match[1]);
	const minutes = Number(match[2]);
	if (!(Number.isFinite(hours) && Number.isFinite(minutes))) {
		return null;
	}

	base.setSeconds(0, 0);
	base.setHours(hours, minutes, 0, 0);
	return base;
};

/** Move `sourceIso` clock time onto `targetDay`'s Y/M/D. */
export const applyClockToDay = (sourceIso: string, targetDay: Date): Date | null => {
	const source = new Date(sourceIso);
	if (Number.isNaN(source.getTime()) || Number.isNaN(targetDay.getTime())) {
		return null;
	}

	const next = new Date(targetDay);
	next.setHours(source.getHours(), source.getMinutes(), source.getSeconds(), source.getMilliseconds());
	return next;
};

export { MIN_RECORD_SAVE_SECONDS };

export const isShortRecordDuration = (elapsedSeconds: number): boolean => elapsedSeconds < MIN_RECORD_SAVE_SECONDS;

/** Start of the current minute (user credit for the whole minute). */
export const recordingStartAt = (now: Date): Date => startOfMinute(now);

/** Start of the current minute, i.e. end of the last completed minute (user debit for the open minute). */
export const recordingEndAt = (now: Date): Date => startOfMinute(now);

/** Stop range: floor start, floor end. Same-minute stop is saved as one minute. */
export const closedTimerRange = (
	start: Date,
	now: Date
): { readonly end: Date; readonly start: Date; readonly timeDiff: number } => {
	const roundedStart = recordingStartAt(start);
	let end = recordingEndAt(now);
	if (end.getTime() <= roundedStart.getTime()) {
		end = addMinutes(roundedStart, 1);
	}
	return {
		start: roundedStart,
		end,
		timeDiff: Math.round((end.getTime() - roundedStart.getTime()) / 1000),
	};
};
